import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import {
  createFreelancerAvailability,
  deleteFreelancerAvailability,
  fetchFreelancerAvailability,
  updateFreelancerAvailability,
} from "../../../redux/features/find-a-job/actions/authAction";
import Loader from "../../loader";

const buildEmptySlot = () => ({
  id: `new-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  start_date: "",
  end_date: "",
  isNew: true,
});

const toInputValue = (dateValue) => {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const getAvailabilityId = (item) => item?.availability_id ?? item?.id;

const getMinDateTime = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const toMidnightString = (dateValue) => {
  if (!dateValue) return null;
  return `${dateValue} 00:00:00`;
};

const toDateOnly = (dateValue) => {
  if (!dateValue) return null;
  return new Date(`${dateValue}T00:00:00`);
};

const hasDateOverlap = (slot, allSlots, ignoreId) => {
  const start = toDateOnly(slot.start_date);
  const end = toDateOnly(slot.end_date || slot.start_date);
  if (!start || !end) return false;
  return allSlots.some((item) => {
    if (!item?.start_date) return false;
    if (ignoreId && item.id === ignoreId) return false;
    const itemStart = toDateOnly(item.start_date);
    const itemEnd = toDateOnly(item.end_date || item.start_date);
    if (!itemStart || !itemEnd) return false;
    return start <= itemEnd && end >= itemStart;
  });
};

const Availability = ({ accountInformation }) => {
  const dispatch = useDispatch();
  const [slots, setSlots] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [errorMap, setErrorMap] = useState({});
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newSlot, setNewSlot] = useState(buildEmptySlot());
  const [newSlotErrors, setNewSlotErrors] = useState({});

  const fetchAvailability = () => {
    setIsLoading(true);
    dispatch(fetchFreelancerAvailability()).then((res) => {
      const payload = res?.payload ?? res;
      const data =
        payload?.data?.availabilities ??
        payload?.data ??
        payload?.availabilities ??
        payload;
      if (!Array.isArray(data)) {
        setSlots([]);
        setIsLoading(false);
        return;
      }
      const normalized = data.map((item) => ({
        id: getAvailabilityId(item),
        start_date: toInputValue(item?.start_date),
        end_date: toInputValue(item?.end_date),
        isNew: false,
      }));
      setSlots(normalized.filter((item) => item.id));
      setIsLoading(false);
    });
  };

  useEffect(() => {
    if (accountInformation !== "Availability") return;
    fetchAvailability();
  }, [accountInformation]);

  const hasSlots = slots.length > 0;

  const validateSlot = (slot, allSlots = [], ignoreId = null) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const start = slot.start_date ? new Date(`${slot.start_date}T00:00:00`) : null;
    const end = slot.end_date ? new Date(`${slot.end_date}T00:00:00`) : null;
    const startPast = start && start < today;
    const endPast = end && end < today;
    const overlap = hasDateOverlap(slot, allSlots, ignoreId);
    return {
      start: !slot.start_date
        ? "Start date is required."
        : startPast
          ? "Start date must be in the future."
          : overlap
            ? "Selected date range overlaps with an existing slot."
          : "",
      end:
        !slot.end_date
          ? ""
          : endPast
            ? "End date must be in the future."
            : start && end && end <= start
              ? "End date must be after start."
              : overlap
                ? "Selected date range overlaps with an existing slot."
                : "",
    };
  };

  const handleSlotChange = (id, field, value) => {
    setSlots((prev) =>
      prev.map((slot) =>
        slot.id === id ? { ...slot, [field]: value } : slot
      )
    );
    setErrorMap((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field === "start_date" ? "start" : "end"]: "" },
    }));
  };

  const handleAddSlot = () => {
    setNewSlot(buildEmptySlot());
    setNewSlotErrors({});
    setIsAddModalOpen(true);
  };

  const handleUpdate = (slot) => {
    const errors = validateSlot(slot, slots, slot.id);
    if (errors.start || errors.end) {
      setErrorMap((prev) => ({ ...prev, [slot.id]: errors }));
      if (errors.start === "Selected date range overlaps with an existing slot." ||
        errors.end === "Selected date range overlaps with an existing slot.") {
        toast.error("Selected date range overlaps with an existing slot.");
      } else {
        toast.error("Please fix the availability errors.");
      }
      return;
    }
    setUpdatingId(slot.id);
    const payload = {
      start_date: toMidnightString(slot.start_date),
      end_date: toMidnightString(slot.end_date),
    };
    const callback = (res) => {
      setUpdatingId(null);
      if (!res?.success && res?.status === false) {
        toast.error(res?.message || "Unable to update availability.");
      } else {
        fetchAvailability();
      }
    };
    dispatch(
      updateFreelancerAvailability({
        availabilityId: slot.id,
        payload,
        callback,
      })
    );
  };

  const handleNewSlotChange = (field, value) => {
    setNewSlot((prev) => ({ ...prev, [field]: value }));
    setNewSlotErrors((prev) => ({
      ...prev,
      [field === "start_date" ? "start" : "end"]: "",
    }));
  };

  const handleCreateFromModal = () => {
    const errors = validateSlot(newSlot, slots, null);
    if (errors.start || errors.end) {
      setNewSlotErrors(errors);
      if (errors.start === "Selected date range overlaps with an existing slot." ||
        errors.end === "Selected date range overlaps with an existing slot.") {
        toast.error("Selected date range overlaps with an existing slot.");
      } else {
        toast.error("Please fix the availability errors.");
      }
      return;
    }
    setUpdatingId(newSlot.id);
    const payload = {
      availabilities: [
        {
          start_date: toMidnightString(newSlot.start_date),
          end_date: toMidnightString(newSlot.end_date),
        },
      ],
    };
    const callback = (res) => {
      setUpdatingId(null);
      if (!res?.success && res?.status === false) {
        toast.error(res?.message || "Unable to add availability.");
      } else {
        setIsAddModalOpen(false);
        fetchAvailability();
      }
    };
    dispatch(createFreelancerAvailability({ payload, callback }));
  };

  const handleDelete = (slotId) => {
    setDeletingId(slotId);
    const callback = (res) => {
      setDeletingId(null);
      if (!res?.success && res?.status === false) {
        toast.error(res?.message || "Unable to delete availability.");
      } else {
        setSlots((prev) => prev.filter((slot) => slot.id !== slotId));
      }
    };
    dispatch(deleteFreelancerAvailability({ availabilityId: slotId, callback }));
  };

  const errorLookup = useMemo(() => errorMap, [errorMap]);

  if (accountInformation !== "Availability") return null;

  if (isLoading || deletingId || updatingId) {
    return <Loader />;
  }
  return (
    <div
      class={`tab-pane fade ${accountInformation == "Availability" && "show active"}`}
      id="v-pills-Availability"
      role="tabpanel"
      aria-labelledby="v-pills-Availability-tab"
    >
      <div className="ct_px_40">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <h4 className="ct_fs_16 ct_darkblue_text">Availability</h4>
            <p className="mb-0 ct_grey_text">
              Manage the time slots when you are available for work.
            </p>
          </div>
          <button
            type="button"
            className="ct_yellow_btn ct_h_44"
            onClick={handleAddSlot}
          >
            Add Slot
          </button>
        </div>

        <div className="mt-4">
          {!hasSlots && !isLoading && (
            <div className="ct_no_factor_Authentication_outline ct_py_15_px_20">
              <p className="mb-0 ct_grey_text">
                No availability added yet.
              </p>
            </div>
          )}

          {slots.map((slot, index) => (
            <div
              key={slot.id}
              className="ct_outline_bg ct_py_15_px_20 mb-3"
            >
              <div className=" mb-2 d-flex align-items-start gap-3 justify-content-between">
                <h6 className="mb-0 ct_fs_18 ct_fw_600 ct_darkblue_text ">
                  Slot {index + 1}
                </h6>


              </div>
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group mb-3">
                    <label className="mb-2 ct_fs_14 ct_grey_text ct_fw_600">
                      Start Date
                    </label>
                    <input
                      type="date"
                      className="form-control ct_input_border_8 "
                      value={slot.start_date} style={{ height: "35px" }}
                      min={getMinDateTime()}
                      onChange={(event) =>
                        handleSlotChange(slot.id, "start_date", event.target.value)
                      }
                    />
                    {errorLookup?.[slot.id]?.start && (
                      <p className="ct_red_text ct_fs_12 mb-0">
                        {errorLookup[slot.id].start}
                      </p>
                    )}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group mb-3">
                    <label className="mb-2 ct_fs_14 ct_grey_text ct_fw_600">
                      End Date
                    </label>
                    <input
                      type="date"
                      className="form-control ct_input_border_8 " style={{ height: "35px" }}
                      value={slot.end_date}
                      min={getMinDateTime()}
                      onChange={(event) =>
                        handleSlotChange(slot.id, "end_date", event.target.value)
                      }
                    />
                    {errorLookup?.[slot.id]?.end && (
                      <p className="ct_red_text ct_fs_12 mb-0">
                        {errorLookup[slot.id].end}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2 justify-content-end">
                <button
                  type="button"
                  className="ct_yellow_btn ct_h_35"
                  onClick={() => handleUpdate(slot)}
                  disabled={updatingId === slot.id}
                >
                  {updatingId === slot.id ? "Updating..." : "Update"}
                </button>
                <button
                  type="button"
                  className="ct_yellow_btn ct_delete_btn_new ct_h_35"
                  onClick={() => handleDelete(slot.id)}
                  disabled={deletingId === slot.id}
                >
                  {deletingId === slot.id ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      {isAddModalOpen && (
        <div className="modal d-block  ct_congratulation_modal_fade">
          <div className="modal-dialog modal-dialog-centered ct_modal-dialog">
            <div className="modal-content">
              <div className="modal-header border-0">
                <h5 className="mb-0 ct_fs_18 ct_darkblue_text ct_fw_700">Add Availability</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setIsAddModalOpen(false)}
                ></button>
              </div>
              <div className="modal-body px-4 pb-4">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="mb-2 ct_fs_14 ct_grey_text ct_fw_600">
                        Start Date
                      </label>
                      <input
                        type="date"
                        className="form-control ct_input_border_8" style={{ height: "40px" }}
                        value={newSlot.start_date}
                        min={getMinDateTime()}
                        onChange={(event) =>
                          handleNewSlotChange("start_date", event.target.value)
                        }
                      />
                      {newSlotErrors?.start && (
                        <p className="ct_red_text ct_fs_12 mb-0">
                          {newSlotErrors.start}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="mb-2 ct_fs_14 ct_grey_text ct_fw_600">
                        End Date (optional)
                      </label>
                      <input
                        type="date"
                        className="form-control ct_input_border_8" style={{ height: "40px" }}
                        value={newSlot.end_date}
                        min={getMinDateTime()}
                        onChange={(event) =>
                          handleNewSlotChange("end_date", event.target.value)
                        }
                      />
                      {newSlotErrors?.end && (
                        <p className="ct_red_text ct_fs_12 mb-0">
                          {newSlotErrors.end}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-end gap-2 mt-2">
                  <button
                    type="button"
                    className="ct_yellow_btn ct_outline_grey ct_h_44"
                    onClick={() => setIsAddModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="ct_yellow_btn ct_h_44"
                    onClick={handleCreateFromModal}
                    disabled={updatingId === newSlot.id}
                  >
                    {updatingId === newSlot.id ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Availability;
